import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface WorkflowEnrollment {
  id: string;
  workflowId: string;
  workflowName: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  currentStep: number;
  totalSteps: number;
  enrolledAt: string;
  lastExecuted?: string;
  nextExecutionAt?: string;
}

export interface SequenceEnrollment {
  id: string;
  sequenceId: string;
  sequenceName: string;
  status: 'active' | 'paused' | 'completed' | 'unsubscribed';
  currentStep: number;
  totalSteps: number;
  enrolledAt: string;
  lastEmailSent?: string;
  nextEmailAt?: string;
  opensCount: number;
  clicksCount: number;
}

const workflowEnrollmentsService = {
  /**
   * Get workflow enrollments for a specific entity
   */
  async getEnrollments(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string
  ): Promise<{ workflows: WorkflowEnrollment[]; sequences: SequenceEnrollment[] }> {
    validateRequiredParams({ organizationId, entityType, entityId }, ['organizationId', 'entityType', 'entityId'], {
      service: 'workflowEnrollmentsService.getEnrollments'
    });

    const url = buildApiUrl('/api/crm/workflow-enrollments', {
      organization_id: organizationId,
      entity_type: entityType,
      entity_id: entityId
    });

    return fetchJSON(url, {
      description: 'Get workflow enrollments',
      context: { organizationId, entityType, entityId }
    });
  },

  /**
   * Pause a workflow enrollment
   */
  async pauseWorkflow(enrollmentId: string, organizationId: string): Promise<{ success: boolean }> {
    validateRequiredParams({ enrollmentId, organizationId }, ['enrollmentId', 'organizationId'], {
      service: 'workflowEnrollmentsService.pauseWorkflow'
    });

    const url = buildApiUrl(`/api/crm/workflow-enrollments/${encodeURIComponent(enrollmentId)}/pause`);

    return fetchJSON(url, {
      method: 'POST',
      body: { organization_id: organizationId },
      description: 'Pause workflow enrollment',
      context: { enrollmentId, organizationId }
    });
  },

  /**
   * Resume a workflow enrollment
   */
  async resumeWorkflow(enrollmentId: string, organizationId: string): Promise<{ success: boolean }> {
    validateRequiredParams({ enrollmentId, organizationId }, ['enrollmentId', 'organizationId'], {
      service: 'workflowEnrollmentsService.resumeWorkflow'
    });

    const url = buildApiUrl(`/api/crm/workflow-enrollments/${encodeURIComponent(enrollmentId)}/resume`);

    return fetchJSON(url, {
      method: 'POST',
      body: { organization_id: organizationId },
      description: 'Resume workflow enrollment',
      context: { enrollmentId, organizationId }
    });
  },

  /**
   * Unenroll from an email sequence
   */
  async unenrollSequence(enrollmentId: string, organizationId: string): Promise<{ success: boolean }> {
    validateRequiredParams({ enrollmentId, organizationId }, ['enrollmentId', 'organizationId'], {
      service: 'workflowEnrollmentsService.unenrollSequence'
    });

    const url = buildApiUrl(`/api/crm/sequence-enrollments/${encodeURIComponent(enrollmentId)}/unenroll`);

    return fetchJSON(url, {
      method: 'POST',
      body: { organization_id: organizationId },
      description: 'Unenroll from sequence',
      context: { enrollmentId, organizationId }
    });
  }
};

export default workflowEnrollmentsService;
